import { useCallback } from 'react';
import { DisplayMode, RowData, ProductLargeCategory } from '../types';
import { calculateHeaderCellWidths } from '../widthCalculator';
import { WIDTH_CONSTRAINTS, TOTAL_SPACING } from '../constants';

/**
 * 幅を最小・最大値内に制限する
 * @param width 元の幅
 * @param constraints 制限値
 * @returns 制限された幅
 */
const constrainWidth = (width: number, constraints: typeof WIDTH_CONSTRAINTS.FIXED_COLUMN | typeof WIDTH_CONSTRAINTS.DATA_COLUMN): number => {
  return Math.min(Math.max(width, constraints.MIN), constraints.MAX);
};

/**
 * レベル値を表示用文字列に変換（画面表示・CSV出力共通）
 * @param levelValue レベル値
 * @returns 表示用文字列（0や空の場合は"-"）
 */
const formatLevelValue = (levelValue: number): string => {
  return (!levelValue || levelValue === 0) ? "-" : levelValue.toString();
};

interface UseColumnWidthsProps {
  headerName: string | undefined;
  rowData: RowData[];
  mode: DisplayMode;
  header: ProductLargeCategory[];
  getDetailColumns: () => any[];
  measureMaxTextWidth: (texts: string[], totalSpacing: number) => number;
  measureTextWidth: (text: string, totalSpacing: number) => number;
}

/**
 * 列幅計算用のカスタムフック
 */
export const useColumnWidths = ({
  headerName,
  rowData,
  mode,
  header,
  getDetailColumns,
  measureMaxTextWidth,
  measureTextWidth
}: UseColumnWidthsProps) => {
  // 固定列幅を計算（ヘッダー名と各行の名前を考慮）
  const calculateFixedColumnWidth = useCallback(() => {
    const names: string[] = [];

    // ヘッダー名を追加
    if (headerName) {
      names.push(headerName);
    }

    // 各行の名前を追加
    rowData.forEach(row => {
      const name = mode === "process" ? row.user_name : row.process_name;
      if (name) {
        names.push(name);
      }
    });

    const totalWidth = measureMaxTextWidth(names, TOTAL_SPACING.FIXED);
    return constrainWidth(totalWidth, WIDTH_CONSTRAINTS.FIXED_COLUMN);
  }, [headerName, rowData, mode, measureMaxTextWidth]);

  // データ列の動的幅を計算
  const calculateDataColumnWidths = useCallback(() => {
    const currentDetailColumns = getDetailColumns();
    if (!currentDetailColumns.length) return [];

    const calculatedWidths: number[] = [];

    currentDetailColumns.forEach((detailCategory) => {
      const textsToMeasure: string[] = [];

      // ヘッダーテキスト（細項目名）を追加
      let headerText = detailCategory.product_detail_category_name || '';
      if (!detailCategory.product_detail_category_name) {
        headerText = '／'; // null項目のデフォルト表示テキスト
      }
      textsToMeasure.push(headerText);

      // データ行の値を追加
      rowData.forEach(row => {
        const levelValue = row.levels.find(level =>
          level.product_large_category_id === detailCategory.product_large_category_id &&
          level.product_medium_category_id === detailCategory.product_medium_category_id &&
          level.product_small_category_id === detailCategory.product_small_category_id &&
          level.product_detail_category_id === detailCategory.product_detail_category_id
        );
        const value = levelValue ? levelValue.product_level_value : 0;
        const displayValue = formatLevelValue(value);
        textsToMeasure.push(displayValue);
      });

      const totalWidth = measureMaxTextWidth(textsToMeasure, TOTAL_SPACING.HEADER);
      const finalWidth = constrainWidth(totalWidth, WIDTH_CONSTRAINTS.DATA_COLUMN);
      calculatedWidths.push(finalWidth);
    });

    return calculatedWidths;
  }, [getDetailColumns, rowData, measureMaxTextWidth]);

  // 上位項目の動的幅を計算
  const calculateHeaderWidths = useCallback((dataColumnWidths: number[]) => {
    return calculateHeaderCellWidths(
      header,
      dataColumnWidths,
      (text: string) => measureTextWidth(text, TOTAL_SPACING.HEADER)
    );
  }, [header, measureTextWidth]);

  return {
    calculateFixedColumnWidth,
    calculateDataColumnWidths,
    calculateHeaderWidths
  };
};
